import { useState, useEffect, useMemo } from "react";
import { ZoomIn, ZoomOut, Download, AlertCircle, Maximize2, Minimize2, Bookmark, Star, ChevronLeft, ChevronRight, X, User, ShoppingCart, Check, Plus, Shield, Crown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { transformDriveUrl, isSupabaseUrl, handleMediaError } from "@/lib/imageUtils";
import { supabase } from "@/integrations/supabase/client";
import { downloadMedia } from "@/lib/downloadUtils";
import { isDownloadExpired } from "@/lib/downloadRules";
import { ExpiredDownloadDialog } from "@/components/ExpiredDownloadDialog";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const extractYoutubeId = (url: string) => {
 const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
 const match = url.match(regExp);
 return (match && match[2].length === 11) ? match[2] : null;
};

interface ImageLightboxProps {
 images: { id: string; url: string; original_url?: string; type: string; assigned_to_user_id?: string | null }[];
 initialIndex: number;
 open: boolean;
 onClose: () => void;
 cartItems?: Set<string>;
 onToggleCart?: (id: string) => void;
 onCheckout?: () => void;
 cartCount?: number;
 cartTotal?: number;
 albumName?: string;
 getMediaPrice?: (id: string) => number;
 watermarkUrl?: string | null;
 watermarkMode?: 'tile' | 'center' | 'fit';
 watermarkOpacity?: number;
 watermarkRotated?: boolean;
 hideWatermark?: boolean;
 watermarkText?: string;
 userPurchases?: Set<string>;
 userPurchaseDates?: Map<string, string>;
 savedMediaIds?: Set<string>;
 userSelections?: Set<string>;
 onToggleBookmark?: (id: string, e: React.MouseEvent) => void;
 isFreeWedding?: boolean;
 antiAiProtection?: boolean;
 antiAiMode?: 'grid' | 'aura';
 previewQuality?: string;
 vipHighQuality?: boolean;
}

export const ImageLightbox = ({ 
 images, initialIndex, open, onClose, cartItems, onToggleCart, onCheckout, 
 cartCount = 0, cartTotal = 0, albumName, getMediaPrice, 
 watermarkUrl, watermarkMode = 'center', watermarkOpacity = 0.5, watermarkRotated = false, 
 hideWatermark = false, watermarkText, userPurchases, userPurchaseDates,
 savedMediaIds, userSelections, onToggleBookmark, isFreeWedding = false, antiAiProtection = false,
 antiAiMode = 'grid',
 previewQuality = "low", vipHighQuality = false
}: ImageLightboxProps) => {
 const [currentIndex, setCurrentIndex] = useState(initialIndex);
 const [loading, setLoading] = useState(true);
 const [showAntiAiPreview, setShowAntiAiPreview] = useState(false);
 const { isSubscriber } = useSubscription();
 const [expiredDialogState, setExpiredDialogState] = useState<{isOpen: boolean, mediaId: string}>({isOpen: false, mediaId: ""});
 const [customWatermark, setCustomWatermark] = useState<string | null>(null);
 const [watermarkSettings, setWatermarkSettings] = useState<any>(null);
 const { user, profile } = useAuth();

 useEffect(() => {
 const fetchBranding = async () => {
 if (watermarkUrl) {
 setCustomWatermark(watermarkUrl);
 }

 const { data: role } = await supabase.from("user_roles").select("user_id").eq("role", "admin").limit(1).maybeSingle();
 if (role) {
 const { data: p } = await supabase.from("profiles")
 .select("watermark_url, watermark_opacity, watermark_rotated")
 .eq("user_id", role.user_id)
 .single();
 
 if (p) {
 setWatermarkSettings(p);
 if (!watermarkUrl && p.watermark_url) {
 setCustomWatermark(p.watermark_url);
 }
 }
 }
 };
 fetchBranding();
 }, [watermarkUrl]);

 useEffect(() => {
 if (open) {
 setCurrentIndex(initialIndex);
 setLoading(true);
 }
 }, [open, initialIndex]);

 useEffect(() => {
 setLoading(true);
 const timeout = setTimeout(() => {
 setLoading(false);
 }, 7000);
 return () => clearTimeout(timeout);
 }, [currentIndex]);

 useEffect(() => {
 if (open) {
 window.history.pushState({ lightbox: true }, "");
 const handlePopState = (e: PopStateEvent) => {
 e.preventDefault();
 onClose();
 };
 window.addEventListener("popstate", handlePopState);
 return () => {
 window.removeEventListener("popstate", handlePopState);
 if (window.history.state?.lightbox) {
 window.history.back();
 }
 };
 }
 }, [open, onClose]);

 const safeIndex = Math.max(0, currentIndex >= 0 && currentIndex < images.length ? currentIndex : 0);
 const current = images[safeIndex] || null;

 const randomBlurSpots = useMemo(() => {
    if (!current?.id || !open) return [];

    // Gerador determinístico baseado no ID para estabilidade na mesma foto
    let seed = 0;
    const str = String(current.id);
    for (let i = 0; i < str.length; i++) {
      seed = (seed * 31 + str.charCodeAt(i)) & 0xffffffff;
    }
    const pseudoRandom = () => {
      seed = (seed * 1664525 + 1013904223) & 0xffffffff;
      return (seed >>> 0) / 4294967296;
    };

    // Grade 12x12 = 144 blocos menores.
    // 40% da área ocupada por pequenos quadrados desfocados e 60% visível para o cliente
    const spots: { top: number; left: number; width: number; height: number; blur: number }[] = [];
    const gridSize = 12;
    const squaresPerRow = 5; // 5 por linha x 12 linhas = 60 blocos (~41% de cobertura)

    for (let r = 0; r < gridSize; r++) {
      const cols = Array.from({ length: gridSize }, (_, i) => i);
      for (let i = cols.length - 1; i > 0; i--) {
        const j = Math.floor(pseudoRandom() * (i + 1));
        [cols[i], cols[j]] = [cols[j], cols[i]];
      }

      const selectedCols = cols.slice(0, squaresPerRow);

      for (const c of selectedCols) {
        // Quadrados menores (~7.8% x 7.8%) com espaçamento proporcional
        const top = r * (100 / gridSize) + 0.25;
        const left = c * (100 / gridSize) + 0.25;
        const width = 100 / gridSize - 0.5;
        const height = 100 / gridSize - 0.5;
        const blur = 8;
        spots.push({ top, left, width, height, blur });
      }
    }

    return spots;
  }, [current?.id, open]);

 if (!open || images.length === 0 || !current) return null;
 const hasPrev = safeIndex > 0;
 const hasNext = safeIndex < images.length - 1;
 const inCart = current ? cartItems?.has(current.id) : false;
 const isPurchased = current ? (userPurchases?.has(current.id) || isFreeWedding) : false;
 const isBookmarked = current ? savedMediaIds?.has(current.id) : false;

 const isAssignedToOther = current?.assigned_to_user_id ? (current.assigned_to_user_id !== user?.id && current.assigned_to_user_id !== profile?.id && current.assigned_to_user_id !== "ALL_VIP") : false;

 const formatPrice = (value: number) => {
 return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
 };

 return (
 <AnimatePresence>
 <motion.div
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 className="fixed inset-0 z-[100] flex items-center justify-center bg-foreground/10 backdrop-blur-md p-4 no-screenshot"
 onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
 >
 <div className="absolute top-0 left-0 right-0 p-6 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent z-10">
 <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-foreground font-mono text-sm px-3 py-1 bg-foreground/10 rounded-full w-fit">
        {safeIndex + 1} <span className="text-foreground/40 ">/</span> {images.length}
      </div>
      {current?.assigned_to_user_id && (
        <div className="bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-black font-black text-xs uppercase tracking-wider px-3 py-1 rounded-full shadow-[0_4px_20px_rgba(255,180,0,0.7)] flex items-center gap-1.5 border border-yellow-200 animate-pulse">
          <Crown className="w-4 h-4 fill-black text-black stroke-[2.5]" />
          <span className="font-extrabold">ASSINANTE VIP</span>
        </div>
      )}
    </div>
    {albumName && <span className="text-[10px] font-black text-foreground/40 uppercase tracking-widest pl-1 mt-1">{albumName}</span>}
 </div>
 
 <button onClick={onClose} className="text-foreground/40 hover:text-foreground transition-all p-3 bg-foreground/10 hover:bg-foreground/10 rounded-2xl border border-foreground/10 shadow-2xl">
 <X className="w-6 h-6" />
 </button>
 </div>



 <ExpiredDownloadDialog 
 isOpen={expiredDialogState.isOpen} 
 onClose={() => setExpiredDialogState({isOpen: false, mediaId: ""})} 
 mediaId={expiredDialogState.mediaId} 
 />

 <div className="absolute bottom-6 left-0 right-0 z-20 px-4 md:hidden flex justify-center pointer-events-none">
 <motion.div 
 initial={{ y: 20, opacity: 0 }}
 animate={{ y: 0, opacity: 1 }}
 className="bg-foreground/10 backdrop-blur-2xl border border-foreground/10 rounded-[32px] p-3 flex items-center gap-3 w-full max-w-sm pointer-events-auto shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
 >
 {isPurchased ? (
 <Button
 onClick={(e) => {
 e.stopPropagation();
 const pDate = userPurchaseDates?.get(current.id);
 if (!isSubscriber && pDate && isDownloadExpired(pDate)) {
 setExpiredDialogState({ isOpen: true, mediaId: current.id });
 return;
 }
 downloadMedia(current.original_url || current.url, `foto_${current.id}.jpg`);
 toast.success("Download iniciado!");
 }}
 className="flex-1 h-16 rounded-2xl font-black uppercase tracking-widest transition-all text-[11px] -white hover:bg-green-600"
 >
 <div className="flex flex-col items-center justify-center gap-1">
 <Download className="w-5 h-5 stroke-[3]" />
 <span>BAIXAR FOTO</span>
 </div>
 </Button>
 ) : (
 <>
 {onToggleBookmark && (
 <Button 
 onClick={(e) => { e.stopPropagation(); onToggleBookmark(current.id, e); }}
 className={cn(
 "w-16 h-16 rounded-2xl flex flex-col items-center justify-center gap-1 transition-all border shrink-0",
 isBookmarked 
 ? "bg-amber-500/20 text-amber-400 border-amber-500/30" 
 : "bg-foreground/10 text-foreground/40 border-foreground/10 "
 )}
 >
 <Bookmark className={cn("w-5 h-5", isBookmarked && "fill-amber-400")} />
 <span className="text-[9px] font-black uppercase tracking-wider">{isBookmarked ? "SALVO" : "SALVAR"}</span>
 </Button>
 )}

  <Button
  onClick={(e) => { 
    e.stopPropagation(); 
    if (isAssignedToOther) {
      toast.error("Esta foto é exclusiva de outro usuário.");
      return;
    }
    onToggleCart?.(current.id); 
  }}
  disabled={isAssignedToOther}
  className={cn(
  "flex-1 h-16 rounded-2xl font-black uppercase tracking-widest transition-all text-[11px]",
  inCart ? "bg-primary text-black" : "bg-foreground/10 text-foreground/40 border border-foreground/10 ",
  isAssignedToOther && "opacity-50 cursor-not-allowed"
  )}
  >
  <div className="flex flex-col items-center justify-center gap-1">
  {isAssignedToOther ? <Shield className="w-5 h-5" /> : (inCart ? <Check className="w-5 h-5 stroke-[4]" /> : <Plus className="w-5 h-5" />)}
  <span>{isAssignedToOther ? "RESERVADA" : (inCart ? "SELECIONADO" : `SELECIONAR ${getMediaPrice ? formatPrice(getMediaPrice(current.id)) : ""}`)}</span>
  </div>
  </Button>

 {(cartCount > 0 || inCart) && (
 <Button
 onClick={(e) => { e.stopPropagation(); onCheckout?.(); }}
 className="flex-1 h-16 bg-white hover:bg-foreground/10 text-black rounded-2xl font-black uppercase tracking-widest shadow-2xl flex flex-col items-center justify-center gap-0.5 text-[11px]"
 >
 <span className="text-[10px] opacity-70 leading-none">
 {cartCount === 0 && inCart ? "1 FOTO" : `${cartCount} ${cartCount === 1 ? 'ITEM' : 'ITENS'}`}
 </span>
 <div className="flex items-center gap-1 leading-none">
 PAGAR {(cartTotal === 0 && cartCount === 0 && inCart && getMediaPrice) ? formatPrice(getMediaPrice(current.id)) : formatPrice(cartTotal)}
 <ChevronRight className="w-4 h-4" />
 </div>
 </Button>
 )}
 </>
 )}
 </motion.div>
 </div>

 <div className="hidden md:flex absolute top-6 right-24 z-10 gap-4">
 {isPurchased ? (
 <Button size="lg" onClick={(e) => {
 e.stopPropagation();
 const pDate = userPurchaseDates?.get(current.id);
 if (!isSubscriber && pDate && isDownloadExpired(pDate)) {
 setExpiredDialogState({ isOpen: true, mediaId: current.id });
 return;
 }
 downloadMedia(current.original_url || current.url, `foto_${current.id}.jpg`);
 toast.success("Download iniciado!");
 }}
 className="h-14 px-8 rounded-2xl font-black uppercase tracking-widest transition-all -white hover:bg-green-600 shadow-[0_10px_30px_rgba(34,197,94,0.3)]">
 <Download className="w-5 h-5 mr-2 stroke-[3]" /> BAIXAR FOTO
 </Button>
 ) : (
 <>
 {onToggleBookmark && (
 <Button 
 size="lg" 
 onClick={(e) => { e.stopPropagation(); onToggleBookmark(current.id, e); }}
 className={cn(
 "h-14 w-14 p-0 rounded-2xl transition-all border shrink-0",
 isBookmarked 
 ? "bg-amber-500/20 text-amber-400 border-amber-500/30 hover:bg-amber-500/30" 
 : "bg-foreground/10 text-foreground/40 border-foreground/10 hover:bg-foreground/10 hover:text-foreground"
 )}
 title={isBookmarked ? "Remover dos salvos" : "Salvar para depois"}
 >
 <Bookmark className={cn("w-5 h-5", isBookmarked && "fill-amber-400")} />
 </Button>
 )}
  {onToggleCart && (
  <Button size="lg" 
  onClick={(e) => { 
    e.stopPropagation(); 
    if (isAssignedToOther) {
      toast.error("Esta foto é exclusiva de outro usuário.");
      return;
    }
    onToggleCart(current.id); 
  }}
  disabled={isAssignedToOther}
  className={cn("h-14 px-8 rounded-2xl font-black uppercase tracking-widest transition-all", 
    inCart ? "bg-primary text-black" : "bg-foreground/10 text-foreground border border-foreground/10 hover:bg-foreground/10 ",
    isAssignedToOther && "opacity-50 cursor-not-allowed"
  )}>
  {isAssignedToOther ? <><Shield className="w-5 h-5 mr-2" /> RESERVADA</> : (inCart ? <><Check className="w-5 h-5 mr-2 stroke-[4]" /> SELECIONADO</> : <><Plus className="w-5 h-5 mr-2" /> SELECIONAR {getMediaPrice && formatPrice(getMediaPrice(current.id))}</>)}
  </Button>
  )}
 {cartCount > 0 && (
 <Button size="lg" onClick={(e) => { e.stopPropagation(); onCheckout?.(); }}
 className="h-14 px-8 bg-white text-black hover:bg-foreground/10 rounded-2xl font-black uppercase tracking-widest shadow-[0_10px_30px_rgba(255,255,255,0.1)]">
 <div className="flex items-center gap-1 leading-none">
 <ShoppingCart className="w-5 h-5 mr-2" /> PAGAR {(cartTotal === 0 && cartCount === 0 && inCart && getMediaPrice) ? formatPrice(getMediaPrice(current.id)) : formatPrice(cartTotal)}
 </div>
 </Button>
 )}
 </>
 )}
 </div>

 {hasPrev && (
 <button onClick={(e) => { e.stopPropagation(); setCurrentIndex(safeIndex - 1); }}
 className="absolute left-4 z-10 text-foreground/40 hover:text-foreground drop-shadow-2xl transition-all hover:scale-110 md:bg-foreground/10 p-2 rounded-2xl">
 <ChevronLeft className="w-10 h-10 md:w-16 md:h-16" />
 </button>
 )}

 {hasNext && (
 <button onClick={(e) => { e.stopPropagation(); setCurrentIndex(safeIndex + 1); }}
 className="absolute right-4 z-10 text-foreground/40 hover:text-foreground drop-shadow-2xl transition-all hover:scale-110 md:bg-foreground/10 p-2 rounded-2xl">
 <ChevronRight className="w-10 h-10 md:w-16 md:h-16" />
 </button>
 )}

 <motion.div
 key={current.id}
 drag="x"
 dragConstraints={{ left: 0, right: 0 }}
 onDragEnd={(_, info) => {
 const threshold = 50;
 if (info.offset.x > threshold && hasPrev) setCurrentIndex(safeIndex - 1);
 else if (info.offset.x < -threshold && hasNext) setCurrentIndex(safeIndex + 1);
 }}
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ type: "spring", damping: 25, stiffness: 200 }}
 className="max-w-[95vw] max-h-[70vh] md:max-h-[80vh] flex items-center justify-center cursor-grab active:cursor-grabbing relative"
 >
 {loading && (
 <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-4">
 <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin shadow-[0_0_20px_rgba(255,106,0,0.4)]" />
 <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em] animate-pulse ">Otimizando...</span>
 </div>
 )}

 {current.type === "video" ? (
 <div className="relative w-full h-full flex items-center justify-center min-w-[320px] md:min-w-[600px] lg:min-w-[800px]">
 {extractYoutubeId(current.url) ? (
 <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-2xl border border-foreground/10 no-screenshot">
 <iframe src={`https://www.youtube.com/embed/${extractYoutubeId(current.url)}?autoplay=1&rel=0&modestbranding=1`} className="w-full h-full" allowFullScreen onLoad={() => setLoading(false)} />
 
 {!hideWatermark && !isPurchased && (
 <div className="absolute inset-0 z-[55] pointer-events-none flex items-center justify-center opacity-100 select-none overflow-hidden mix-blend-screen">
 {customWatermark ? (
 watermarkMode === 'tile' ? (
 <div 
 className="absolute w-[90%] h-[90%] top-[5%] left-[5%] pointer-events-none select-none"
 style={{ 
 backgroundImage: `url(${customWatermark})`,
 backgroundRepeat: 'no-repeat',
 backgroundSize: 'contain', 
 backgroundPosition: 'center',
 transform: "none",
 opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6,
 filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.6)) contrast(1.1) brightness(1.1)'
 }}
 />
 ) : (
 <div 
 className={cn(
 "transition-all duration-500",
 watermarkMode === 'fit' ? "w-full h-full flex items-center justify-center" : "w-full h-full flex items-center justify-center",
                      "rotate-0"
 )}
 style={{ opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6 }}
 >
 {watermarkMode === 'fit' ? (
 [...Array(1)].map((_, i) => (
 <div key={i} className="relative w-[90%] aspect-square flex items-center justify-center shrink-0">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 ))
 ) : (
 <div className="relative w-1/2 aspect-square max-w-[400px] flex items-center justify-center">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 )}
 </div>
 )
 ) : (
 <div className="w-[250%] h-[250%] flex flex-wrap items-center justify-center gap-10 rotate-[-35deg] opacity-20 drop-shadow-[0_4px_10px_rgba(0,0,0,1)]">
 {[...Array(40)].map((_, i) => (
 <p key={i} className="text-4xl md:text-5xl font-black  uppercase tracking-tighter text-foreground whitespace-nowrap">
 {watermarkText || "By Hudson Raone"}
 </p>
 ))}
 </div>
 )}
 </div>
 )}
 </div>
 ) : (
 <>
 <video 
 src={(() => {
 const url = current.url || "";
 if (url.includes('drive.google.com') || url.includes('docs.google.com')) {
 const id = url.match(/[-\w]{25,}/)?.[0];
 return id ? `https://drive.google.com/uc?export=download&id=${id}` : url;
 }
 if (/^[-\w]{25,40}$/.test(url)) {
 return `https://drive.google.com/uc?export=download&id=${url}`;
 }
 return url;
 })()} 
 autoPlay 
 controls 
 onLoadedData={() => setLoading(false)} 
 controlsList="nodownload" 
 onContextMenu={(e) => e.preventDefault()}
 onError={(e) => {
 const video = e.target as HTMLVideoElement;
 if (video.src && video.src.includes('drive.google.com')) {
 const id = video.src.match(/id=([-\w]{25,})/)?.[1];
 if (id) {
 console.warn("Retrying video with alternative stream link...");
 video.src = `https://drive.google.com/u/0/uc?id=${id}&export=download`;
 }
 }
 setLoading(false);
 }}
 className="max-w-full max-h-[70vh] md:max-h-[80vh] rounded-2xl shadow-2xl border border-foreground/10 no-screenshot" 
 />
 {!hideWatermark && !isPurchased && (
 <div className="absolute inset-0 z-[55] pointer-events-none flex items-center justify-center opacity-100 select-none overflow-hidden mix-blend-screen">
 {customWatermark ? (
 watermarkMode === 'tile' ? (
 <div 
 className="absolute w-[90%] h-[90%] top-[5%] left-[5%] pointer-events-none select-none"
 style={{ 
 backgroundImage: `url(${customWatermark})`,
 backgroundRepeat: 'no-repeat',
 backgroundSize: 'contain', 
 backgroundPosition: 'center',
 transform: "none",
 opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6,
 filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.6)) contrast(1.1) brightness(1.1)'
 }}
 />
 ) : (
 <div 
 className={cn(
 "transition-all duration-500",
 watermarkMode === 'fit' ? "w-full h-full flex items-center justify-center" : "w-full h-full flex items-center justify-center",
                      "rotate-0"
 )}
 style={{ opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6 }}
 >
 {watermarkMode === 'fit' ? (
 [...Array(1)].map((_, i) => (
 <div key={i} className="relative w-[90%] aspect-square flex items-center justify-center shrink-0">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 ))
 ) : (
 <div className="relative w-1/2 aspect-square max-w-[400px] flex items-center justify-center">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 )}
 </div>
 )
 ) : (
 <div className="w-[250%] h-[250%] flex flex-wrap items-center justify-center gap-10 rotate-[-35deg] opacity-20 drop-shadow-[0_4px_10px_rgba(0,0,0,1)]">
 {[...Array(40)].map((_, i) => (
 <p key={i} className="text-4xl md:text-5xl font-black  uppercase tracking-tighter text-foreground whitespace-nowrap">
 {watermarkText || "By Hudson Raone"}
 </p>
 ))}
 </div>
 )}
 </div>
 )}
 </>
 )}
 </div>
 ) : (
 <div className="relative group/protection overflow-hidden rounded-3xl">
 <img 
    src={(() => {
        let size = 400; // default low
        
        // 1. Check if it's assigned to a subscriber (has assigned_to_user_id)
        const isAssignedToSubscriber = Boolean(current.assigned_to_user_id);
        
        if (isPurchased) {
          size = 3000;
        } else if (vipHighQuality && isAssignedToSubscriber) {
          // Priority: Assigned to VIP -> High Quality
          size = 3000;
        } else {
          // Album Default Setting
          if (previewQuality === "high") size = 3000;
          else if (previewQuality === "medium") size = 800;
          else size = 400;
        }
        
        return transformDriveUrl(current.url || (current as any).thumbnail_url || current.original_url, size);
      })()} 
    alt="" 
    onLoad={() => setLoading(false)} 
    onError={(e) => {
      setLoading(false);
      handleMediaError(e, current as any, isPurchased ? 1600 : 400);
    }}
    onContextMenu={(e) => e.preventDefault()} 
    className={cn(
      "w-full h-full max-w-full max-h-[70vh] md:max-h-[80vh] object-contain rounded-3xl shadow-[0_30px_100px_rgba(0,0,0,0.8)] border border-foreground/10 select-none no-drag transition-opacity duration-300", 
      loading ? "opacity-0" : "opacity-100",
      !isPurchased && "filter blur-[1px] contrast-[1.2] saturate-[0.8] sepia-[0.3]"
    )} 
  />
 
 {/* Anti-AI / Adversarial Noise Layer */}
 <div className="absolute inset-0 z-[52] pointer-events-none opacity-[0.03] mix-blend-difference" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`, backgroundSize: '100px 100px' }} />
 
 {/* Invisible Forensic Watermark */}
 <div className="absolute inset-0 z-[53] pointer-events-none opacity-[0.02] mix-blend-overlay flex flex-wrap overflow-hidden select-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' width='100' height='100'%3E%3Ctext x='10' y='50' font-family='monospace' font-size='10' fill='white' transform='rotate(-45 50 50)'%3EPROTECTED%3C/text%3E%3C/svg%3E")`, backgroundSize: '80px 80px' }} />

 <div className="absolute inset-0 z-[60] bg-transparent cursor-default select-none group-active/protection:bg-foreground/10 " onContextMenu={(e) => e.preventDefault()} />
 
 {/* Camada de Desfoque Anti-IA & Anti-Print (Blocos ou Áurea em Movimento) */}
  {(showAntiAiPreview || (!hideWatermark && !isPurchased && antiAiProtection)) && (
    antiAiMode === "aura" ? (
      <div className="absolute inset-0 z-[54] pointer-events-none overflow-hidden select-none rounded-3xl">
        {/* Faixa de onda de desfoque (~40% da foto) que varre a imagem, sem bordas nem formato de círculo */}
        <motion.div
          className="absolute left-[-15%] w-[130%] h-[40%]"
          animate={{
            top: ["-15%", "75%", "-15%"],
            rotate: [-5, 5, -5],
            scaleY: [1, 1.25, 1],
          }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          style={{
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%, #000 70%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, #000 30%, #000 70%, transparent 100%)',
          }}
        />
        {/* Segunda onda em fase oposta, mais suave */}
        <motion.div
          className="absolute left-[-15%] w-[130%] h-[28%]"
          animate={{
            top: ["80%", "-10%", "80%"],
            rotate: [4, -4, 4],
            scaleY: [1.2, 0.9, 1.2],
          }}
          transition={{ duration: 13, repeat: Infinity, ease: "easeInOut" }}
          style={{
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 50%, transparent 100%)',
            maskImage: 'linear-gradient(to bottom, transparent 0%, #000 50%, transparent 100%)',
          }}
        />
      </div>
    ) : (
      <div className="absolute inset-0 z-[54] pointer-events-none overflow-hidden select-none">
        {randomBlurSpots.map((spot, idx) => (
          <div 
            key={idx}
            style={{
              top: `${spot.top}%`,
              left: `${spot.left}%`,
              width: `${spot.width}%`,
              height: `${spot.height}%`,
              backdropFilter: `blur(${spot.blur}px)`,
              WebkitBackdropFilter: `blur(${spot.blur}px)`,
            }}
            className="absolute rounded-md bg-black/10 border border-white/10 shadow-sm transition-all duration-200" 
          />
        ))}
      </div>
    )
  )}
  
  {/* Photo Watermark Overlay */}
 {!hideWatermark && !isPurchased && !isSupabaseUrl(current.url) && (
 <div className="absolute inset-0 z-[55] pointer-events-none flex items-center justify-center opacity-100 select-none overflow-hidden">
 {customWatermark ? (
 watermarkMode === 'tile' ? (
 <div 
 className="absolute w-[90%] h-[90%] top-[5%] left-[5%] pointer-events-none select-none"
 style={{ 
 backgroundImage: `url(${customWatermark})`,
 backgroundRepeat: 'no-repeat',
 backgroundSize: 'contain', // Uniform tile size
 backgroundPosition: 'center',
 transform: "none",
 opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6,
 filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.6)) contrast(1.1) brightness(1.1)'
 }}
 />
 ) : (
 <div 
 className={cn(
 "transition-all duration-500",
 watermarkMode === 'fit' ? "w-full h-full flex items-center justify-center" : "w-full h-full flex items-center justify-center",
                      "rotate-0"
 )}
 style={{ opacity: watermarkOpacity ?? watermarkSettings?.watermark_opacity ?? 0.6 }}
 >
 {watermarkMode === 'fit' ? (
 [...Array(1)].map((_, i) => (
 <div key={i} className="relative w-[90%] aspect-square flex items-center justify-center shrink-0">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_10px_30px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 ))
 ) : (
 <div className="relative w-1/2 aspect-square max-w-[400px] flex items-center justify-center">
 <img src={customWatermark} className="absolute inset-0 w-full h-full object-contain drop-shadow-[0_20px_50px_rgba(0,0,0,0.9)] contrast-[1.2] brightness-125" alt="watermark" />
 </div>
 )}
 </div>
 )
 ) : (
 <div className="w-[250%] h-[250%] flex flex-wrap items-center justify-center gap-10 rotate-[-35deg] opacity-20 drop-shadow-[0_4px_10px_rgba(0,0,0,1)]">
 {[...Array(40)].map((_, i) => (
 <p key={i} className="text-4xl md:text-5xl font-black  uppercase tracking-tighter text-foreground whitespace-nowrap">
 {watermarkText || "By Hudson Raone"}
 </p>
 ))}
 </div>
 )}
 </div>
 )}

 {!isPurchased && (
  <div className="absolute bottom-4 md:bottom-8 left-0 right-0 z-[65] flex justify-center pointer-events-none px-4">
    <div className="bg-primary/90 backdrop-blur-md px-4 py-2 rounded-xl border border-yellow-200/50 shadow-[0_10px_30px_rgba(255,165,0,0.5)] text-center animate-pulse">
      <span className="text-[10px] md:text-xs font-black text-black uppercase tracking-widest flex items-center justify-center gap-2">
        <Star className="w-3.5 h-3.5 fill-black shrink-0" />
        A FOTO COMPRADA SERÁ BAIXADA EM MÁXIMA QUALIDADE (4K)
        <Star className="w-3.5 h-3.5 fill-black shrink-0" />
      </span>
    </div>
  </div>
 )}

 <div className="absolute inset-0 z-[70] opacity-0 group-active/protection:opacity-100 pointer-events-none flex items-center justify-center transition-opacity">
 <div className="bg-foreground/10 backdrop-blur-md px-6 py-3 rounded-full border border-red-500/50 shadow-[0_0_20px_rgba(255,0,0,0.3)]">
 <span className="text-[10px] font-black text-red-400 uppercase tracking-widest  flex items-center gap-2">
 <Shield className="w-3 h-3" /> Imagem Protegida ©
 </span>
 </div>
 </div>
 </div>
 )}
 </motion.div>
 </motion.div>
 </AnimatePresence>
 );
};
